import { Badge, Button, PaginationProps, Popconfirm, Space, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import React, { useMemo } from 'react';
import { useTranslation } from '../../i18n/i18n';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import './DevicesGeneralTable.scss';
import useAuthStore from '../../stores/auth.store';
import EmptyState from '../utils/EmptyState/EmptyState';
import useTenantStore, { ALL_TENANTS_VALUE, TENANT_COL_KEY } from '../../stores/tenant.store';

export type GeneralDevice = {
  uuid: string;
  name: string;
  connect: boolean;
  connected_at: string;
  type: string;
  fleet: string;
  location: string;
  sensorType?: number;
  sewerLocation?: number;
  ultrasonicModel?: number;
  driverModel?: number;
  tenant?: string;
  tenantUuid?: string;
};

type DeviceGeneralTableProps = {
  data: readonly GeneralDevice[];
  onDeleteDevice: (id: string) => void;
  onEditDevice: (record: GeneralDevice) => void;
  onSeeHistory: (uuid: string) => void;
  pagination: PaginationProps;
  onChange: (pagination: PaginationProps) => void;
  loading?: boolean;
};

const DeviceGeneralTable = (props: DeviceGeneralTableProps) => {
  const { t } = useTranslation('devices', { keyPrefix: 'table' });
  const { t: tCommon } = useTranslation('common');
  const isSuperAdmin = useAuthStore((state) => state.isSuperAdmin);
  const isSuperAdminOrCompanyAdmin = useAuthStore((state) => state.isSuperAdminOrCompanyAdmin);
  const isAll = useTenantStore((state) => state.selectedTenant === ALL_TENANTS_VALUE);

  const { onDeleteDevice, onEditDevice, onSeeHistory } = props;

  const columns = useMemo<ColumnsType<GeneralDevice>>(
    () => [
      {
        title: t('id'),
        dataIndex: 'name',
        key: 'name',
        width: '25%',
        render: (value, record) => (
          <Badge status={record.connect ? 'success' : 'error'} text={value} className={'MainTableText'}></Badge>
        ),
      },

      {
        title: t('type'),
        dataIndex: 'type',
        key: 'type',
        width: '15%',
      },
      {
        title: t('model'),
        dataIndex: 'fleet',
        key: 'fleet',
        width: '25%',
      },
      {
        title: t('tenant'),
        dataIndex: TENANT_COL_KEY,
        key: TENANT_COL_KEY,
        width: '15%',
      },
      {
        title: t('ubication'),
        dataIndex: 'location',
        key: 'location',
        width: '15%',
      },
      {
        title: t('actions'),
        dataIndex: 'location',
        key: 'location',
        width: '30%',
        onCell: () => {
          return {
            onClick: (e) => {
              e.stopPropagation();
            },
          };
        },
        render: (value, record) => (
          <Space className={'DeviceGeneralTable__colHistory'}>
            {isSuperAdminOrCompanyAdmin && (
              <Button
                type={'text'}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditDevice(record);
                }}
              >
                <EditOutlined />
              </Button>
            )}
            {isSuperAdmin && (
              <Popconfirm
                title={t('deleteTitle')}
                description={t('deleteDevice', { name: record.name })}
                okText={t('deleteOk')}
                okButtonProps={{ danger: true }}
                cancelText={t('deleteCancel')}
                placement={'left'}
                onConfirm={() => {
                  onDeleteDevice(record.uuid);
                }}
              >
                <Button
                  type={'text'}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <DeleteOutlined />
                </Button>
              </Popconfirm>
            )}
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onSeeHistory(record.uuid);
              }}
            >
              {t('seeHistory')}
            </Button>
          </Space>
        ),
      },
    ],
    [t, isSuperAdminOrCompanyAdmin, isSuperAdmin, onDeleteDevice, onEditDevice, onSeeHistory],
  ).filter((col) => !(col.key === TENANT_COL_KEY && !isAll));

  return (
    <Table
      bordered={false}
      columns={columns}
      dataSource={props.data}
      loading={props.loading}
      rowKey={(record) => record.uuid}
      pagination={{
        ...props.pagination,
      }}
      onChange={props.onChange}
      locale={{ emptyText: <EmptyState title={tCommon('emptyTable')} /> }}
    />
  );
};

export default DeviceGeneralTable;
